import { useCallback, useMemo, useState } from 'react';

export type SiteCreationStep =
  | 'step1'
  | 'construction'
  | 'agency'
  | 'contacts';

export interface UseSiteCreationStepperOptions {
  /** 현재 조직 category. 'SAFETY_MANAGEMENT_AGENCY'면 기관(agency) Step 노출 */
  organizationCategory?: string;
  /**
   * Step 1 입력 중인 업종이 건설업인가 — true면 construction Step 노출. 판정은 폼 상태
   * (`useSiteFormState`의 `isConstructionIndustry` → `lib/siteIndustry`)가 소유한다(KSIC 대분류 F ·
   * 없으면 레거시 「건설업」). 이 훅은 그 결과만 받는다 — 판정을 두 벌 두지 않게.
   */
  isConstructionIndustry?: boolean;
}

export interface UseSiteCreationStepperResult {
  step: SiteCreationStep;
  showConstruction: boolean;
  showAgency: boolean;
  goToStep: (step: SiteCreationStep) => void;
  goNext: () => void;
  goBack: () => void;
  reset: () => void;
  stepIndex: number;
  totalSteps: number;
  /** 현재 단계가 마지막 선택 단계(완료 버튼 노출) 인지 */
  isLastStep: boolean;
  /** 현재 단계가 첫 단계(이전 버튼 없음) 인지 */
  isFirstStep: boolean;
  /** 현재 활성 단계 키 배열 (노출 순서대로) */
  visibleSteps: SiteCreationStep[];
}

export function useSiteCreationStepper(
  options: UseSiteCreationStepperOptions,
): UseSiteCreationStepperResult {
  const { organizationCategory, isConstructionIndustry } = options;

  const showConstruction = isConstructionIndustry === true;
  const showAgency = organizationCategory === 'SAFETY_MANAGEMENT_AGENCY';

  const visibleSteps = useMemo<SiteCreationStep[]>(() => {
    const steps: SiteCreationStep[] = ['step1'];
    if (showConstruction) steps.push('construction');
    if (showAgency) steps.push('agency');
    steps.push('contacts');
    return steps;
  }, [showConstruction, showAgency]);

  const [step, setStep] = useState<SiteCreationStep>('step1');

  const goToStep = useCallback((next: SiteCreationStep) => {
    setStep(next);
  }, []);

  const goNext = useCallback(() => {
    setStep((prev) => {
      const idx = visibleSteps.indexOf(prev);
      if (idx < 0 || idx === visibleSteps.length - 1) return prev;
      return visibleSteps[idx + 1];
    });
  }, [visibleSteps]);

  const goBack = useCallback(() => {
    setStep((prev) => {
      const idx = visibleSteps.indexOf(prev);
      if (idx <= 0) return prev;
      return visibleSteps[idx - 1];
    });
  }, [visibleSteps]);

  const reset = useCallback(() => {
    setStep('step1');
  }, []);

  const stepIndex = useMemo(() => {
    const idx = visibleSteps.indexOf(step);
    return idx < 0 ? 0 : idx;
  }, [step, visibleSteps]);

  const totalSteps = visibleSteps.length;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === totalSteps - 1;

  return {
    step,
    showConstruction,
    showAgency,
    goToStep,
    goNext,
    goBack,
    reset,
    stepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    visibleSteps,
  };
}
